import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  BookOnline as BookOnlineIcon,
  Build as BuildIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { collection, getDocs, query, orderBy, limit, where, Timestamp, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * @param {object} [props.source] another country's data (see utils/indiaOperationsSource.js);
 *   without it the page reads the Australian database as before.
 */
const DashboardHome = ({ source = null }) => {
  const navigate = useNavigate();
  const currencySymbol = source ? source.currencySymbol : '$';
  // India pages live under /dashboard/india/... (same page names).
  const routeFor = (path) => (source ? path.replace('/dashboard/', '/dashboard/india/') : path);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalProviders: 0,
    totalServices: 0,
    totalBookings: 0,
    completedBookings: 0,
    pendingBookings: 0
  });
  const [latestBookings, setLatestBookings] = useState([]);
  const [latestServices, setLatestServices] = useState([]);
  const [latestUsers, setLatestUsers] = useState([]);
  // dialog & filter states for full-details + download
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsType, setDetailsType] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const newestFirst = (a, b) => {
    const ta = a.createdAt && a.createdAt.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
    const tb = b.createdAt && b.createdAt.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
    return tb - ta;
  };

  // Same numbers as the Australian page, from the country's overview data.
  const fetchSourceDashboardData = async () => {
    const { users, services, bookings } = await source.loadOverview();
    const isCustomer = (u) => (u.roles && (u.roles.customer === true || u.roles.customer === 'true')) || u.role === 'customer';
    const isProvider = (u) => (u.roles && (u.roles.client === true || u.roles.provider === true)) || u.role === 'client' || u.role === 'provider';
    // India bookings wait for the provider in "upcoming" (no "pending" step).
    const pendingStatuses = ['pending', 'upcoming'];
    setLatestServices([...services].sort(newestFirst).slice(0, 3));
    setLatestBookings([...bookings].sort(newestFirst).slice(0, 3));
    setLatestUsers(users.filter(isCustomer).sort(newestFirst).slice(0, 3));
    setStats({
      totalCustomers: users.filter(isCustomer).length,
      totalProviders: users.filter(isProvider).length,
      totalServices: services.length,
      totalBookings: bookings.length,
      completedBookings: bookings.filter((b) => b.status?.toLowerCase() === 'completed').length,
      pendingBookings: bookings.filter((b) => pendingStatuses.includes(b.status?.toLowerCase())).length,
    });
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      if (source) {
        await fetchSourceDashboardData();
        return;
      }

      // Fetch all users once and compute customers/providers counts locally to avoid index errors
      let totalCustomers = 0;
      let totalProviders = 0;
      let allUsers = [];
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        usersSnapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data() };
          allUsers.push(data);
          const roles = data.roles || {};
          // normalize possible keys
          if (roles.customer === true || roles.customer === 'true' || data.role === 'customer') totalCustomers++;
          if (roles.client === true || roles.client === 'true' || roles.provider === true || roles.provider === 'true' || data.role === 'client' || data.role === 'provider') totalProviders++;
        });
      } catch (e) {
        // fallback: try legacy 'customers' or 'clients' collections
        try {
          const custSnap = await getDocs(collection(db, 'customers'));
          totalCustomers = custSnap.size;
        } catch (err) {}
        try {
          const clientsSnap = await getDocs(collection(db, 'clients'));
          totalProviders = clientsSnap.size;
        } catch (err) {}
      }

      // Fetch services
      const servicesSnapshot = await getDocs(collection(db, 'services'));
      const totalServices = servicesSnapshot.size;

      // Fetch latest services
      const servicesQuery = query(collection(db, 'services'), orderBy('createdAt', 'desc'), limit(3));
      const servicesQuerySnapshot = await getDocs(servicesQuery);
      const servicesData = [];
      servicesQuerySnapshot.forEach((doc) => {
        servicesData.push({ id: doc.id, ...doc.data() });
      });
      setLatestServices(servicesData);

      // Fetch bookings
      const bookingsSnapshot = await getDocs(collection(db, 'bookings'));
      let completedCount = 0;
      let pendingCount = 0;
      bookingsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.status?.toLowerCase() === 'completed') completedCount++;
        else if (data.status?.toLowerCase() === 'pending') pendingCount++;
      });

      // Fetch latest bookings
      const bookingsQuery = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'), limit(3));
      const bookingsQuerySnapshot = await getDocs(bookingsQuery);
      const bookingsData = [];
      bookingsQuerySnapshot.forEach((doc) => {
        bookingsData.push({ id: doc.id, ...doc.data() });
      });
      setLatestBookings(bookingsData);

      // Compute latest users (customers) from allUsers collected above
      try {
        const customerUsers = allUsers.filter(u => (u.roles && (u.roles.customer === true || u.roles.customer === 'true')) || u.role === 'customer');
        // sort by createdAt (handle Timestamp or string)
        customerUsers.sort((a, b) => {
          const ta = a.createdAt && a.createdAt.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
          const tb = b.createdAt && b.createdAt.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
          return tb - ta;
        });
        setLatestUsers(customerUsers.slice(0, 3));
      } catch (e) {
        setLatestUsers([]);
      }

      // debug log to help diagnose any data mismatches in development
      try {
        // eslint-disable-next-line no-console
        console.log('Dashboard counts', { totalCustomers, totalProviders, totalServices, totalBookings: bookingsSnapshot.size, completedCount, pendingCount });
      } catch (e) {}

      setStats({
        totalCustomers,
        totalProviders,
        totalServices,
        totalBookings: bookingsSnapshot.size,
        completedBookings: completedCount,
        pendingBookings: pendingCount
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (type) => {
    setDetailsType(type);
    setFilterStart('');
    setFilterEnd('');
    setFilterSearch('');
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
  };

  const matchesSearch = (doc, term) => {
    if (!term) return true;
    const s = term.toString().toLowerCase();
    const fields = [
      doc.name,
      doc.displayName,
      doc.email,
      doc.title,
      doc.serviceName,
      doc.serviceTitle,
      doc.customerName,
      doc.phone,
      doc.username,
      doc.company,
      doc.description
    ];
    for (const f of fields) {
      if (f && f.toString().toLowerCase().includes(s)) return true;
    }
    try {
      const raw = JSON.stringify(doc).toLowerCase();
      return raw.includes(s);
    } catch (e) {
      return false;
    }
  };

  const formatCsvValue = (val) => {
    if (val === null || val === undefined) return '';
    if (val.toDate && typeof val.toDate === 'function') return val.toDate().toISOString();
    if (val instanceof Date) return val.toISOString();
    const s = String(val);
    return '"' + s.replace(/"/g, '""') + '"';
  };

  // Australian rows for the details dialog: the first candidate collection
  // that returns documents (shared by the preview and the download).
  const fetchAuRows = async (candidates) => {
    let rows = [];
    for (const collName of candidates) {
      try {
        const constraints = [];
        if (filterStart) constraints.push(where('createdAt', '>=', Timestamp.fromDate(new Date(filterStart))));
        if (filterEnd) constraints.push(where('createdAt', '<=', Timestamp.fromDate(new Date(filterEnd))));
        let qref;
        try {
          if (constraints.length) qref = query(collection(db, collName), ...constraints, orderBy('createdAt', 'desc'));
          else qref = query(collection(db, collName), orderBy('createdAt', 'desc'));
        } catch (e) {
          qref = query(collection(db, collName));
        }
        const snap = await getDocs(qref);
        if (!snap.empty) {
          snap.forEach((doc) => rows.push({ id: doc.id, __from: collName, ...doc.data() }));
          break;
        }
      } catch (e) {
        // try next candidate
        // eslint-disable-next-line no-console
        console.debug('collection query failed for', collName, e);
      }
    }

    // If no rows with constraints, try unconstrained read on candidate collections (helps when createdAt type mismatches)
    if (rows.length === 0) {
      for (const collName of candidates) {
        try {
          const snap = await getDocs(query(collection(db, collName), orderBy('createdAt', 'desc')));
          if (!snap.empty) {
            snap.forEach((doc) => rows.push({ id: doc.id, __from: collName, ...doc.data() }));
            break;
          }
        } catch (e) {
          try {
            // last resort: try without orderBy
            const snap2 = await getDocs(collection(db, collName));
            if (!snap2.empty) {
              snap2.forEach((doc) => rows.push({ id: doc.id, __from: collName, ...doc.data() }));
              break;
            }
          } catch (err) {
            // continue
          }
        }
      }
    }

    // if still no rows found at all, try a last-resort fetch from 'users' without constraints
    if (rows.length === 0 && !candidates.includes('users')) {
      try {
        const snap = await getDocs(collection(db, 'users'));
        snap.forEach((doc) => rows.push({ id: doc.id, __from: 'users', ...doc.data() }));
      } catch (e) {}
    }
    return rows;
  };

  const downloadData = async () => {
    setDownloadLoading(true);
    try {
      // Determine candidate collections to query in order of preference
      let candidates = [];
      if (detailsType === 'customers') candidates = ['customers', 'users'];
      else if (detailsType === 'providers') candidates = ['clients', 'providers', 'users'];
      else if (detailsType === 'services') candidates = ['services'];
      else if (detailsType === 'bookings') candidates = ['bookings'];
      else candidates = [detailsType || 'users'];

      let rows = source ? await source.overviewRows(detailsType) : await fetchAuRows(candidates);

      // client-side filtering for roles/search and fallback date parsing
      let filtered = rows.filter((r) => {
        // If rows came from a dedicated collection (customers/clients/services/bookings), do not role-filter
        const fromDedicated = r.__from && ['customers', 'clients', 'services', 'bookings', 'providers'].includes(r.__from);
        if (!fromDedicated && (detailsType === 'customers')) {
          const roles = r.roles || {};
          if (!(roles.customer === true || roles.customer === 'true' || r.role === 'customer')) return false;
        }
        if (!fromDedicated && (detailsType === 'providers')) {
          const roles = r.roles || {};
          if (!(roles.client === true || roles.client === 'true' || roles.provider === true || roles.provider === 'true' || r.role === 'client' || r.role === 'provider')) return false;
        }

        // date fallback filtering
        if (filterStart || filterEnd) {
          const created = r.createdAt && r.createdAt.toDate ? r.createdAt.toDate() : (r.createdAt ? new Date(r.createdAt) : null);
          if (filterStart && created && created < new Date(filterStart)) return false;
          if (filterEnd && created && created > new Date(filterEnd + 'T23:59:59')) return false;
        }

        // search term
        if (filterSearch) {
          if (!matchesSearch(r, filterSearch)) return false;
        }

        return true;
      });

      // If filtering removed everything but there were rows retrieved, relax and allow download of raw rows (useful when role keys differ)
      if (filtered.length === 0 && rows.length > 0) {
        // eslint-disable-next-line no-console
        console.warn('No rows matched role filters; falling back to raw rows for download.');
        filtered = rows.slice();
      }

      // proceed to build CSV from `filtered`

      // Build CSV
      let headers = [];
      let dataRows = [];
      if (detailsType === 'customers' || detailsType === 'providers') {
        headers = ['id', 'name', 'email', 'role', 'createdAt'];
        dataRows = filtered.map(r => [r.id, r.name || '', r.email || '', r.role || JSON.stringify(r.roles || {}), r.createdAt]);
      } else if (detailsType === 'services') {
        headers = ['id', 'title', 'category', 'price', 'createdAt'];
        dataRows = filtered.map(r => [r.id, r.title || r.name || '', r.category || '', r.price ?? '', r.createdAt]);
      } else if (detailsType === 'bookings') {
        headers = ['id', 'serviceName', 'customerName', 'date', 'price', 'status'];
        dataRows = filtered.map(r => [r.id, r.serviceName || r.serviceTitle || '', r.customerName || r.customerId || '', r.createdAt, r.price ?? r.totalAmount ?? '', r.status || '']);
      }

      const csv = [headers.join(',')]
        .concat(dataRows.map(row => row.map(formatCsvValue).join(',')))
        .join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${detailsType || 'export'}-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Download failed', err);
      alert('Download failed. Check console for details.');
    } finally {
      setDownloadLoading(false);
    }
  };

  // Fetch a small preview of rows when dialog opens or filters change
  const fetchPreview = async () => {
    if (!detailsOpen) return;
    setPreviewLoading(true);
    try {
      let candidates = [];
      if (detailsType === 'customers') candidates = ['customers', 'users'];
      else if (detailsType === 'providers') candidates = ['clients', 'providers', 'users'];
      else if (detailsType === 'services') candidates = ['services'];
      else if (detailsType === 'bookings') candidates = ['bookings'];
      else candidates = [detailsType || 'users'];

      let rows = source ? await source.overviewRows(detailsType) : await fetchAuRows(candidates);

      // apply same client-side filtering as download
      let filtered = rows.filter((r) => {
        const fromDedicated = r.__from && ['customers', 'clients', 'services', 'bookings', 'providers'].includes(r.__from);
        if (!fromDedicated && (detailsType === 'customers')) {
          const roles = r.roles || {};
          if (!(roles.customer === true || roles.customer === 'true' || r.role === 'customer')) return false;
        }
        if (!fromDedicated && (detailsType === 'providers')) {
          const roles = r.roles || {};
          if (!(roles.client === true || roles.client === 'true' || roles.provider === true || roles.provider === 'true' || r.role === 'client' || r.role === 'provider')) return false;
        }
        if (filterStart || filterEnd) {
          const created = r.createdAt && r.createdAt.toDate ? r.createdAt.toDate() : (r.createdAt ? new Date(r.createdAt) : null);
          if (filterStart && created && created < new Date(filterStart)) return false;
          if (filterEnd && created && created > new Date(filterEnd + 'T23:59:59')) return false;
        }
        if (filterSearch) {
          if (!matchesSearch(r, filterSearch)) return false;
        }
        return true;
      });

      if (filtered.length === 0 && rows.length > 0) filtered = rows.slice();
      // Enrich preview rows: for providers, attach one service; for services, attach provider name when possible
      const previewLimit = 15;
      const sliceRows = filtered.slice(0, previewLimit);
      if (!source && detailsType === 'providers') {
        // For each provider row, try to find one service that references this provider
        for (const r of sliceRows) {
          const pid = r.id || r.providerId || r.userId || r.uid || r.uid?.toString();
          if (!pid) continue;
          try {
            // try several indexed service fields that may reference provider id
            const tryFields = ['providerId', 'ownerId', 'createdBy', 'userId', 'owner_id'];
            let found = false;
            for (const f of tryFields) {
              try {
                const q = query(collection(db, 'services'), where(f, '==', pid), orderBy('createdAt', 'desc'), limit(1));
                const snap = await getDocs(q);
                if (snap && !snap.empty) {
                  const doc0 = snap.docs[0];
                  const s = doc0.data();
                  r._servicePreview = s.title || s.name || s.serviceName || '';
                  found = true;
                  break;
                }
              } catch (e) {
                // ignore index/field errors and continue trying other fields
              }
            }
            // try nested provider id fields if not found via top-level fields
            if (!found) {
              const nestedFields = ['provider.id', 'provider.uid', 'providerId', 'provider'];
              for (const nf of nestedFields) {
                try {
                  const q = query(collection(db, 'services'), where(nf, '==', pid), orderBy('createdAt', 'desc'), limit(1));
                  const snap = await getDocs(q);
                  if (snap && !snap.empty) {
                    const doc0 = snap.docs[0];
                    const s = doc0.data();
                    r._servicePreview = s.title || s.name || s.serviceName || '';
                    found = true;
                    break;
                  }
                } catch (e) {
                  // not all nested fields are queryable; ignore errors
                }
              }
            }
            // Last-resort: fetch a small recent set of services and attempt a client-side match
            if (!found) {
              try {
                const recent = await getDocs(query(collection(db, 'services'), orderBy('createdAt', 'desc'), limit(20)));
                for (const sd of recent.docs) {
                  const s = sd.data();
                  // client-side heuristics: match common id fields or provider email/name
                  const sidMatch = (s.providerId && s.providerId === pid) || (s.ownerId && s.ownerId === pid) || (s.userId && s.userId === pid) || (s.createdBy && s.createdBy === pid) || (s.provider && (s.provider === pid || s.provider.id === pid || s.provider.uid === pid));
                  const contactMatch = (r.email && (s.providerEmail === r.email || s.contactEmail === r.email)) || (r.name && (s.providerName === r.name || s.ownerName === r.name));
                  if (sidMatch || contactMatch) {
                    r._servicePreview = s.title || s.name || s.serviceName || '';
                    found = true;
                    break;
                  }
                }
              } catch (e) {
                // ignore
              }
            }
          } catch (e) {
            // ignore per-row errors
          }
        }
      }
      if (!source && detailsType === 'services') {
        // For each service row, try to attach provider name from common fields or by looking up users
          for (const r of sliceRows) {
          // try common fields first
          r._providerName = r.providerName || r.provider?.name || r.ownerName || r.createdByName || r.providerDisplayName || '';
          if (r._providerName) continue;
            const pid = r.providerId || r.provider || r.ownerId || r.createdBy || r.userId || r.provider?.id || r.provider?.uid;
            if (!pid) continue;
            try {
              // prefer direct document lookup when provider id is a document id
              try {
                const userDoc = await getDoc(doc(db, 'users', pid));
                if (userDoc && userDoc.exists()) {
                  const u = userDoc.data();
                  r._providerName = u.name || u.displayName || u.email || '';
                  continue;
                }
              } catch (e) {
                // fall back to querying users collection if direct lookup failed
              }
              // fall back to query by common fields (email/name)
              const usersSnap = await getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(5)));
              for (const ud of usersSnap.docs) {
                const u = ud.data();
                if (u.email && r.providerEmail && u.email === r.providerEmail) {
                  r._providerName = u.name || u.displayName || u.email || '';
                  break;
                }
                if (r.providerName && (u.name === r.providerName || u.displayName === r.providerName)) {
                  r._providerName = u.name || u.displayName || u.email || '';
                  break;
                }
              }
            } catch (e) {
              // ignore
            }
        }
      }

      setPreviewRows(sliceRows);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Preview fetch failed', e);
      setPreviewRows([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  // update preview when dialog opens or when filters change
  useEffect(() => {
    if (detailsOpen) fetchPreview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsOpen, filterStart, filterEnd, filterSearch, detailsType]);

  const formatDate = (dateField) => {
    if (!dateField) return 'N/A';
    if (dateField.toDate && typeof dateField.toDate === 'function') {
      return dateField.toDate().toLocaleDateString();
    }
    if (dateField instanceof Date) {
      return dateField.toLocaleDateString();
    }
    if (typeof dateField === 'string') {
      try {
        return new Date(dateField).toLocaleDateString();
      } catch {
        return dateField;
      }
    }
    // Support RN-style date map objects: { day, month, year }
    if (
      typeof dateField === "object" &&
      dateField !== null &&
      dateField.day !== undefined &&
      dateField.month !== undefined &&
      (dateField.year !== undefined || dateField.Y !== undefined)
    ) {
      const day = String(dateField.day).padStart(2, "0");
      const month = String(dateField.month).padStart(2, "0");
      const year = String(dateField.year || dateField.Y);
      return `${day}/${month}/${year}`;
    }
    return 'N/A';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      {/* <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: '1.75rem' }}>
          Welcome to Admin Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Use the sidebar to navigate to different sections:
        </Typography>
      </Box> */}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <Card onClick={() => openDetails('customers')} sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e8e8e8', cursor: 'pointer' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  backgroundColor: '#E3F2FD', 
                  borderRadius: 2, 
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <PeopleIcon sx={{ color: '#1976d2', fontSize: 30 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                    Total Customers
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '2rem' }}>
                    {stats.totalCustomers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

    <Grid size={{ xs: 6, sm: 6, md: 3 }}>
      <Card onClick={() => openDetails('providers')} sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e8e8e8', cursor: 'pointer' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  backgroundColor: '#F3E5F5', 
                  borderRadius: 2, 
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <BusinessIcon sx={{ color: '#7b1fa2', fontSize: 30 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                    Service Providers
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '2rem' }}>
                    {stats.totalProviders}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

    <Grid size={{ xs: 6, sm: 6, md: 3 }}>
      <Card onClick={() => openDetails('services')} sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e8e8e8', cursor: 'pointer' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  backgroundColor: '#E8F5E9', 
                  borderRadius: 2, 
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <BuildIcon sx={{ color: '#2e7d32', fontSize: 30 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                    Total Services
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '2rem' }}>
                    {stats.totalServices}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

    <Grid size={{ xs: 6, sm: 6, md: 3 }}>
      <Card onClick={() => openDetails('bookings')} sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e8e8e8', cursor: 'pointer' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  backgroundColor: '#FFF3E0', 
                  borderRadius: 2, 
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <BookOnlineIcon sx={{ color: '#f57c00', fontSize: 30 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                    Total Bookings
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '2rem' }}>
                    {stats.totalBookings}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Latest Bookings */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Latest Bookings
          </Typography>
          <Button 
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate(routeFor('/dashboard/bookings'))}
            sx={{ textTransform: 'none' }}
          >
            View All
          </Button>
        </Box>
        <Card sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e8e8e8' }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Booking #</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Service</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {latestBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No bookings found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  latestBookings.map((booking) => (
                    <TableRow key={booking.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          #{booking.orderNumber || booking.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {booking.serviceName || booking.serviceTitle || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {booking.customerName || booking.customerId || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(booking.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          {currencySymbol}{parseFloat(booking.price || booking.totalAmount || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={booking.status || 'Pending'}
                          color={getStatusColor(booking.status)}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      {/* Latest Services and Users */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Latest Services
            </Typography>
            <Button 
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(routeFor('/dashboard/services'))}
              sx={{ textTransform: 'none' }}
            >
              View All
            </Button>
          </Box>
          <Card sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e8e8e8' }}>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#fafafa' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Service</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {latestServices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No services found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    latestServices.map((service) => (
                      <TableRow key={service.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {service.title || service.name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={service.category || 'General'} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                            {currencySymbol}{parseFloat(service.price || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Latest Users
            </Typography>
            <Button 
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(routeFor('/dashboard/customers'))}
              sx={{ textTransform: 'none' }}
            >
              View All
            </Button>
          </Box>
          <Card sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e8e8e8' }}>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#fafafa' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Joined</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {latestUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No users found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    latestUsers.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, backgroundColor: '#4A5BF4' }}>
                              {user.name?.charAt(0) || user.email?.charAt(0).toUpperCase() || 'U'}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {user.name || 'User'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {user.email || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(user.createdAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
      {/* Details dialog for full exports */}
      <Dialog open={detailsOpen} onClose={closeDetails} fullWidth maxWidth="sm">
        <DialogTitle>
          {detailsType ? `${detailsType.charAt(0).toUpperCase() + detailsType.slice(1)} - Full Details` : 'Full Details'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            <TextField
              label="Start Date"
              type="date"
              value={filterStart}
              onChange={(e) => setFilterStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="End Date"
              type="date"
              value={filterEnd}
              onChange={(e) => setFilterEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="Search"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              placeholder="name, email, service..."
              sx={{ minWidth: 240 }}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Use date range and search to filter results. Click "Download CSV" to export filtered data.
            </Typography>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview</Typography>
            {previewLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}><CircularProgress size={18} /></Box>
            ) : previewRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No preview results</Typography>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 220 }}>
                <Table size="small">
                  {detailsType === 'customers' && (
                    <>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewRows.map((r) => (
                          <TableRow key={r.id} hover>
                            <TableCell>{(r.name || r.displayName || r.email || '—').toString().slice(0, 36)}</TableCell>
                            <TableCell>{formatDate(r.createdAt)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </>
                  )}

                  {detailsType === 'providers' && (
                    <>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewRows.map((r) => (
                          <TableRow key={r.id} hover>
                            <TableCell>{(r.name || r.displayName || r.email || '—').toString().slice(0, 36)}</TableCell>
                            <TableCell>{(r._servicePreview || r.serviceName || r.serviceTitle || r.title || 'No service yet').toString().slice(0, 36)}</TableCell>
                            <TableCell>{formatDate(r.createdAt)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </>
                  )}

                  {detailsType === 'services' && (
                    <>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewRows.map((r) => (
                          <TableRow key={r.id} hover>
                            <TableCell>{(r.title || r.name || '').toString().slice(0, 36)}</TableCell>
                            <TableCell>{(r._providerName || r.providerName || (r.provider && r.provider.name) || '').toString().slice(0,36)}</TableCell>
                            <TableCell>{formatDate(r.createdAt)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </>
                  )}

                  {detailsType === 'bookings' && (
                    <>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewRows.map((r) => (
                          <TableRow key={r.id} hover>
                            <TableCell>{(r.customerName || r.name || r.displayName || r.email || '—').toString().slice(0, 36)}</TableCell>
                            <TableCell>{(r.serviceName || r.serviceTitle || r.title || '').toString().slice(0, 36)}</TableCell>
                            <TableCell>{formatDate(r.createdAt)}</TableCell>
                            <TableCell>{r.status || r.state || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </>
                  )}
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              closeDetails();
              const route = detailsType === 'customers' ? '/dashboard/customers' : detailsType === 'providers' ? '/dashboard/clients' : detailsType === 'services' ? '/dashboard/services' : '/dashboard/bookings';
              navigate(routeFor(route));
            }}
            sx={{ textTransform: 'none' }}
          >
            View All
          </Button>
          <Button onClick={downloadData} variant="contained" disabled={downloadLoading} sx={{ textTransform: 'none' }}>
            {downloadLoading ? <CircularProgress size={18} color="inherit" /> : 'Download CSV'}
          </Button>
          <Button onClick={closeDetails} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardHome;
